import { DateRange, DateTime } from '@ksyos/date-time';
import {
    Badge,
    Button,
    FormParagraph,
    IconButton,
    Select,
    Spacer,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
    Text,
} from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import type { IReduxState } from '@frontend/configureStore';
import type { ValidationErrors } from '@frontend/utils';
import { getDisplayName } from '@common/utils/getDisplayName';
import { isActiveOnDate } from '@common/utils/isActiveOnDate';
import { DateInput } from '@frontend/components/fields';
import { translateKey } from '@frontend/translate';
import { getValidationErrorTranslation, useAbortableFetch } from '@frontend/utils';

interface IServiceOrganizationGroupHistoryProps {
    errorKey: string;
    errors: ValidationErrors;
    onChange: (value: IServiceOrganizationGroupHistory[]) => void;
    onDoValidate?: (validationErrors: Map<string, string | null>) => void;
    serviceId?: string;
    serviceOrganizationGroupHistory: IServiceOrganizationGroupHistory[];
    serviceOrganizationSettingId: string;
}

export interface IServiceOrganizationGroupHistoryChangeItem {
    errors: Map<string, string | null>;
    id: IServiceOrganizationGroupHistory['id'];
    name: keyof IServiceOrganizationGroupHistory;
    value: IServiceOrganizationGroupHistory[keyof IServiceOrganizationGroupHistory];
}

interface IServiceGroupSetting {
    group: IServiceGroups;
    groupId: string;
    id: string;
}

export interface IServiceOrganizationGroupHistoryPractitioner {
    id: string;
    nameFamily: string;
    nameGiven: string | null;
    nameInfix: string | null;
}

export interface IServiceOrganizationGroupHistory {
    endDate: DateTime | null;
    id: string;
    isDeleted: boolean;
    isNew: boolean;
    lastUpdatedByPractitioner: IServiceOrganizationGroupHistoryPractitioner | null;
    lastUpdatedByPractitionerId: string | null;
    serviceGroupSetting?: IServiceGroupSetting;
    serviceGroupSettingId?: string;
    serviceOrganizationSettingId: string;
    startDate: DateTime | null;
}

interface IServiceGroups {
    id: string;
    name: string;
}

export function ServiceOrganizationGroupHistorySelector({
    errorKey,
    serviceOrganizationSettingId,
    onChange,
    serviceId,
    serviceOrganizationGroupHistory,
    errors,
    onDoValidate,
}: IServiceOrganizationGroupHistoryProps) {
    const abortableFetch = useAbortableFetch();
    const practitioner = useSelector((state: IReduxState) => state.authentication);

    const [serviceGroupSettings, setServiceGroupSettings] = useState<IServiceGroupSetting[]>([]);

    useEffect(() => {
        if (serviceId) {
            loadServiceGroupSettings(serviceId);
        }
    }, [serviceId]);

    if (!serviceId) {
        return null;
    }

    if (serviceGroupSettings.length < 1) {
        return <></>;
    }

    const groupHistory = (() => {
        const sorted = serviceOrganizationGroupHistory
            .filter((item) => !(item.isDeleted && item.isNew))
            .sort((a, b) =>
                (a.startDate ?? DateTime.from('9999-12-31')).isSameOrAfter(b.startDate ?? DateTime.from('9999-12-31'))
                    ? -1
                    : 1
            );

        const { accepted, deleted } = sorted.reduce<{ [key: string]: IServiceOrganizationGroupHistory[] }>(
            (result, current) => {
                if (current.isDeleted) {
                    result.deleted.push(current);
                } else {
                    result.accepted.push(current);
                }

                return result;
            },
            {
                accepted: [],
                deleted: [],
            }
        );

        return [...accepted, ...deleted];
    })();

    return (
        <>
            <Button
                iconLeft="add"
                onClick={addNewServiceOrganizationGroupHistory}
                style={{ margin: '16px 0' }}
                variant="link"
            >
                Zorggroep toevoegen
            </Button>

            {groupHistory.filter((item) => !(item.isDeleted && item.isNew)).length > 0 && (
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableHeader>Zorggroep</TableHeader>
                            <TableHeader>Aangepast door</TableHeader>
                            <TableHeader>Start- en einddatum</TableHeader>
                            <TableHeader></TableHeader>
                            <TableHeader></TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {groupHistory.map((serviceOrganizationGroup) => {
                            const {
                                id,
                                serviceGroupSettingId,
                                lastUpdatedByPractitioner,
                                startDate,
                                endDate,
                                isNew,
                                isDeleted,
                            } = serviceOrganizationGroup;

                            return (
                                <TableRow key={`row-${id}`}>
                                    <TableCell style={{ verticalAlign: 'top' }}>
                                        {isNew && (
                                            <Select
                                                error={getValidationErrorTranslation(
                                                    errors.get(`${errorKey}.${id}.serviceGroupSetting`)
                                                )}
                                                onChange={(value: { label: string; value: string } | null) => {
                                                    if (value === null) {
                                                        return;
                                                    }

                                                    const found = serviceGroupSettings.find(
                                                        (item) => item.id === value.value
                                                    );

                                                    if (found) {
                                                        handleServiceOrganizationGroupHistoryChange(
                                                            id,
                                                            'serviceGroupSettingId',
                                                            found.id
                                                        );

                                                        handleServiceOrganizationGroupHistoryChange(
                                                            id,
                                                            'serviceGroupSetting',
                                                            found
                                                        );
                                                    }
                                                }}
                                                options={serviceGroupSettings.map((item) => ({
                                                    value: item.id,
                                                    label: item.group.name,
                                                }))}
                                                value={(() => {
                                                    const found = serviceGroupSettings.find(
                                                        (item) => item.id === serviceGroupSettingId
                                                    );

                                                    if (found) {
                                                        return {
                                                            value: found.id,
                                                            label: found.group.name,
                                                        };
                                                    }

                                                    return null;
                                                })()}
                                            />
                                        )}
                                        {!isNew && (
                                            <Text
                                                as="label"
                                                color={isDeleted ? 'disabled' : 'default'}
                                                fontWeight="bold"
                                                py={2}
                                            >
                                                {
                                                    serviceGroupSettings.find(
                                                        (item) => item.id === serviceGroupSettingId
                                                    )?.group.name
                                                }
                                            </Text>
                                        )}
                                    </TableCell>
                                    <TableCell style={{ verticalAlign: 'top' }}>
                                        <Text
                                            as="label"
                                            color={isDeleted ? 'disabled' : 'default'}
                                            fontWeight="bold"
                                            py={2}
                                        >
                                            {lastUpdatedByPractitioner
                                                ? getDisplayName(
                                                      lastUpdatedByPractitioner,
                                                      false,
                                                      'nameGivenNameFamily'
                                                  )
                                                : '-'}
                                        </Text>
                                    </TableCell>
                                    <TableCell style={{ verticalAlign: 'top' }}>
                                        {isDeleted && (
                                            <Spacer
                                                direction="horizontal"
                                                style={{ padding: '7px 0px' }}
                                            >
                                                <Text color={isDeleted ? 'disabled' : 'default'}>
                                                    {startDate?.format('YYYY-MM-DD') ?? '-'}
                                                </Text>
                                                <Text color={isDeleted ? 'disabled' : 'default'}>
                                                    {translateKey('common.until')}
                                                </Text>
                                                <Text color={isDeleted ? 'disabled' : 'default'}>
                                                    {endDate?.format('YYYY-MM-DD') ?? '-'}
                                                </Text>
                                            </Spacer>
                                        )}
                                        {!isDeleted && (
                                            <Spacer direction="horizontal">
                                                <DateInput
                                                    error={errors.get(`${errorKey}.${id}.startDate`)}
                                                    onChange={(value) => {
                                                        handleServiceOrganizationGroupHistoryChange(
                                                            id,
                                                            'startDate',
                                                            value ? DateTime.from(value) : null
                                                        );
                                                    }}
                                                    placeholder="dd-mm-jjjj"
                                                    value={startDate?.format('YYYY-MM-DD') ?? null}
                                                    variant="outline"
                                                />
                                                <FormParagraph>{translateKey('common.until')}</FormParagraph>
                                                <DateInput
                                                    error={errors.get(`${errorKey}.${id}.endDate`)}
                                                    onChange={(value) => {
                                                        handleServiceOrganizationGroupHistoryChange(
                                                            id,
                                                            'endDate',
                                                            value ? DateTime.from(value) : null
                                                        );
                                                    }}
                                                    placeholder="dd-mm-jjjj"
                                                    value={endDate?.format('YYYY-MM-DD') ?? null}
                                                    variant="outline"
                                                />
                                            </Spacer>
                                        )}
                                    </TableCell>
                                    <TableCell style={{ verticalAlign: 'top' }}>
                                        {!isDeleted && (
                                            <IconButton
                                                icon="delete"
                                                onClick={() => deleteNewServiceOrganizationGroupHistory(id)}
                                                size="small"
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell style={{ verticalAlign: 'top' }}>
                                        {startDate &&
                                            !isDeleted &&
                                            isActiveOnDate(serviceOrganizationGroup, DateTime.now()) && (
                                                <Badge
                                                    color="green"
                                                    size="small"
                                                    text="Huidige"
                                                />
                                            )}
                                        {isDeleted && (
                                            <Badge
                                                size="small"
                                                text="Verwijderd"
                                            />
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}
        </>
    );

    function handleServiceOrganizationGroupHistoryChange<K extends keyof IServiceOrganizationGroupHistory>(
        id: IServiceOrganizationGroupHistory['id'],
        name: K,
        value: IServiceOrganizationGroupHistory[K]
    ) {
        onChange(
            serviceOrganizationGroupHistory.map((entry) => {
                if (entry.id === id) {
                    entry[name] = value;
                }

                return entry;
            })
        );

        onDoValidate?.(validateServiceOrganizationGroupHistorySelector(serviceOrganizationGroupHistory, id, name));
    }

    function addNewServiceOrganizationGroupHistory() {
        const id = uuidv4();
        onChange([
            {
                id,
                serviceOrganizationSettingId,
                startDate: null,
                endDate: null,
                lastUpdatedByPractitionerId: practitioner.id,
                lastUpdatedByPractitioner: practitioner,
                isDeleted: false,
                isNew: true,
            },
            ...serviceOrganizationGroupHistory,
        ]);
    }

    function deleteNewServiceOrganizationGroupHistory(id: IServiceOrganizationGroupHistory['id']) {
        onChange(
            serviceOrganizationGroupHistory
                .map((item) => {
                    if (item.id === id) {
                        return { ...item, isDeleted: true };
                    }

                    return item;
                })
                .filter((item) => !(item.isDeleted && item.isNew))
        );

        onDoValidate?.(validateServiceOrganizationGroupHistorySelector(serviceOrganizationGroupHistory, id, 'deleted'));
    }

    async function loadServiceGroupSettings(id: string) {
        const result = await abortableFetch.fetchJson(`/api/serviceGroupSettings?serviceId=${id}&isActive=true`, 'GET');

        setServiceGroupSettings(
            result.json.items.map((item: IServiceGroupSetting) => ({
                id: item.id,
                groupId: item.groupId,
                group: item.group,
            }))
        );
    }
}

export function validateServiceOrganizationGroupHistorySelector(
    state: IServiceOrganizationGroupHistory[],
    id?: IServiceOrganizationGroupHistory['id'],
    name?: keyof IServiceOrganizationGroupHistory | 'deleted'
) {
    let validationErrors = new Map<string, string[]>();

    if (id) {
        const changedItem = state.find((item) => item.id === id);

        if (changedItem) {
            if (name === 'serviceGroupSettingId') {
                validationErrors = mergeValidationErrors(
                    validationErrors,
                    validateServiceOrganizationGroupHistoryGroup(changedItem)
                );
            } else if (name === 'startDate') {
                validationErrors = mergeValidationErrors(
                    validationErrors,
                    validateServiceOrganizationGroupHistoryStartDate(changedItem),
                    validateServiceOrganizationGroupHistoryEndDate(changedItem)
                );
            } else if (name === 'endDate') {
                validationErrors = mergeValidationErrors(
                    validationErrors,
                    validateServiceOrganizationGroupHistoryEndDate(changedItem)
                );
            }

            validationErrors = mergeValidationErrors(
                validationErrors,
                datesHaveOverlap(
                    state.filter((entry) => !(name === 'deleted' && entry.id === id)),
                    true
                )
            );
        }
    } else {
        state.forEach((item) => {
            validationErrors = mergeValidationErrors(
                validationErrors,
                validateServiceOrganizationGroupHistoryStartDate(item),
                validateServiceOrganizationGroupHistoryEndDate(item),
                validateServiceOrganizationGroupHistoryGroup(item)
            );
            validationErrors = mergeValidationErrors(validationErrors, datesHaveOverlap(state));
        });
    }

    return new Map(Array.from(validationErrors).map(([key, value]) => [key, value[0] ?? null]));
}
function mergeValidationErrors(...validationErrors: Map<string, string[]>[]) {
    const allValidationErrors = new Map<string, string[]>();

    validationErrors.forEach((validationError) => {
        validationError.forEach((value, key) => {
            const set = allValidationErrors.get(key) ?? [];
            allValidationErrors.set(key, [...set, ...value]);
        });
    });

    return allValidationErrors;
}

function validateServiceOrganizationGroupHistoryGroup({
    id,
    serviceGroupSettingId,
    isDeleted,
}: IServiceOrganizationGroupHistory) {
    const validationErrors = new Map<string, string[]>();

    const set: string[] = [];

    if (!isDeleted && !serviceGroupSettingId) {
        set.push('any_empty');
    }

    validationErrors.set(`${id}.serviceGroupSetting`, set);

    return validationErrors;
}

function validateServiceOrganizationGroupHistoryStartDate({
    id,
    startDate,
    isDeleted,
}: IServiceOrganizationGroupHistory) {
    const validationErrors = new Map<string, string[]>();

    const set: string[] = [];

    if (!isDeleted && startDate === null) {
        set.push('koi_missingStartDate');
    }

    validationErrors.set(`${id}.startDate`, set);

    return validationErrors;
}

function validateServiceOrganizationGroupHistoryEndDate({
    id,
    startDate,
    endDate,
    isDeleted,
}: IServiceOrganizationGroupHistory) {
    const validationErrors = new Map<string, string[]>();

    const set: string[] = [];

    if (!isDeleted && startDate !== null && endDate !== null && !DateTime.from(endDate).isSameOrAfter(startDate)) {
        set.push('koi_endDate');
    }

    validationErrors.set(`${id}.endDate`, set);

    return validationErrors;
}

function datesHaveOverlap(state: IServiceOrganizationGroupHistory[], validOnly: boolean = false) {
    const validationErrors = new Map<string, string[]>();

    const toBeChecked = state
        .filter((item) => {
            if (validOnly) {
                return !item.isDeleted && item.startDate;
            }

            return !item.isDeleted;
        })
        .map((item) => ({
            ...item,
            range: new DateRange(
                item.startDate ?? DateTime.from('1900-01-01'),
                item.endDate ?? DateTime.from('9999-12-31')
            ),
        }));

    do {
        const first = toBeChecked.pop();

        if (first) {
            toBeChecked.forEach((second) => {
                const firstSet: string[] = validationErrors.get(`${first.id}.endDate`) ?? [];
                const secondSet: string[] = validationErrors.get(`${second.id}.endDate`) ?? [];

                if (first.range.hasOverlapWith(second.range)) {
                    firstSet.push('dateOverlap');
                    secondSet.push('dateOverlap');
                }

                validationErrors.set(`${first.id}.endDate`, firstSet);
                validationErrors.set(`${second.id}.endDate`, secondSet);
            });
        }
    } while (toBeChecked.length);

    return validationErrors;
}
